import React, { useState, useEffect } from "react";
import { ReactSVG } from "react-svg";
import { Spin, Row, Col, Button, Steps, Modal, Form, Image } from "antd";
import moment from "moment";
import { Link } from "react-router-dom";
import jsPDF from "jspdf";
import { renderToStaticMarkup } from "react-dom/server";
import { isEmpty, filter } from "lodash";
import { intersection, pullAll } from "lodash";

import "./OrdersDetail.scss";
import ItemContainer from "../../modules/order/containers/Item";
import withPage from "./withPage";
import StopsMapbox from "../Mapbox/StopsMapbox";
import { getStopsOrder, getProgressStatus } from "../../utils/helper";
import OrderInfoForm from "../Forms/Orders/OrderInfo";
import PickupForm from "../Forms/Orders/Pickup";
import DeliveryForm from "../Forms/Orders/Delivery";
import markerIcons from "../Markers/markerIcons";
import OrderExportTemplate from "../ExportTemplates/OrderExportTemplate";
import { getTranslation } from "../../utils/helper";
import LabelComponent from "../Elements/LabelComponent";

import {
  TIME_FORMAT_FRONTEND,
  DATE_FORMAT_FRONTEND,
  DATETIME_FORMAT_FRONTEND,
  DATE_FORMAT_QUERY,
  STATUS,
} from "../../constants";
import Cookies from "js-cookie";
import roboto from "../../fonts/Roboto-Regular-normal.js";

const { Step } = Steps;

const defaultCountryCode = window.location.href.includes("nordicrouting.se") ? "se" : "sg";

const OrdersDetailPage = ({
  id,
  orderCode,
  isFetching,
  message,
  order,
  doUpdateOrder,
  doDetailTrackingOrder,
  groups,
  doGetAllGroups,
  user,
}) => {
  const setting = user?.company || {};
  const [form] = Form.useForm();
  const [stopClicked, setStopClicked] = useState(null);
  const [stops, setStops] = useState([]);
  const [state, setState] = useState({
    visibleModal: false,
    action: "",
  });
  const canEdit = !isEmpty(user) && !user?.roles?.includes("user");
  const status = order?.status?.toLowerCase();

  const [visible, setVisible] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [countryCodeInit, setCountryCodeInit] = useState(defaultCountryCode);

  useEffect(() => {
    setCountryCodeInit(Cookies.get(`CountryCode-${user._id}`) || defaultCountryCode);
  }, []);

  useEffect(() => {
    if (canEdit) {
      doGetAllGroups();
    }
  }, []);

  useEffect(() => {
    if (orderCode) {
      doDetailTrackingOrder(orderCode);
    }
  }, [orderCode]);

  useEffect(() => {
    if ((order?._id === id || order?.orderCode === orderCode) && (order?.pickupLat || order?.deliveryLat)) {
      setStops(getStopsOrder(order, "641EFD"));
    }
  }, [order]);

  useEffect(() => {
    if (!isFetching && message?.includes("update_succeeded")) {
      handleCancel();
    }
  }, [isFetching]);

  const onClosePopup = () => {
    setStopClicked({ id: "closed" });
  };

  const handleClickAction = (action, item) => {
    const params = {
      ...item,
      driver: item?.driver?._id,
      group: item?.group?._id,
      pickupAddress: {
        address: item?.pickupAddress,
        latLng: { lat: item?.pickupLat, lng: item?.pickupLng },
      },
      deliveryAddress: {
        address: item?.deliveryAddress,
        latLng: { lat: item?.deliveryLat, lng: item?.deliveryLng },
      },
      deliveryDate: item?.deliveryDate ? moment(item?.deliveryDate).utc() : null,
      timeStart: item?.timeStart ? moment(item?.timeStart, TIME_FORMAT_FRONTEND) : null,
      timeEnd: item?.timeEnd ? moment(item?.timeEnd, TIME_FORMAT_FRONTEND) : null,
      deliveryTimeStart: item?.deliveryTimeStart ? moment(item?.deliveryTimeStart, TIME_FORMAT_FRONTEND) : null,
      deliveryTimeEnd: item?.deliveryTimeEnd ? moment(item?.deliveryTimeEnd, TIME_FORMAT_FRONTEND) : null,
      types: item?.requiredSkills || [],
    };
    const fields = item?.fields || [];
    params.fields = Object.keys(fields).map(key => ({
      name: key,
      value: fields[key],
    }));
    form.setFieldsValue(params);
    setState({
      ...state,
      action,
      visibleModal: true,
    });
  };

  const handleSubmit = async values => {
    const params = setParams(values);
    await doUpdateOrder({ id: order._id, ...params });
  };

  const handleCancel = () => {
    setState({ ...state, visibleModal: false });
    form.resetFields();
  };

  const setParams = values => {
    const params = { ...values };
    if (state.action === "order_info") {
      params.deliveryDate = values.deliveryDate ? values.deliveryDate.format(DATE_FORMAT_QUERY) : null;
    }
    if (state.action === "pickup") {
      params.pickupAddress = values.pickupAddress?.address;
      params.pickupLat = values.pickupAddress?.latLng?.lat;
      params.pickupLng = values.pickupAddress?.latLng?.lng;
      params.timeStart = values.timeStart ? values.timeStart.format(TIME_FORMAT_FRONTEND) : null;
      params.timeEnd = values.timeEnd ? values.timeEnd.format(TIME_FORMAT_FRONTEND) : null;
      params.pickupDuration = values.pickupDuration ? Number(values.pickupDuration) : null;
    }
    if (state.action === "delivery") {
      params.deliveryAddress = values.deliveryAddress?.address;
      params.deliveryLat = values.deliveryAddress?.latLng?.lat;
      params.deliveryLng = values.deliveryAddress?.latLng?.lng;
      params.deliveryTimeStart = values.deliveryTimeStart
        ? values.deliveryTimeStart.format(TIME_FORMAT_FRONTEND)
        : null;
      params.deliveryTimeEnd = values.deliveryTimeEnd ? values.deliveryTimeEnd.format(TIME_FORMAT_FRONTEND) : null;
      params.deliveryDuration = values.deliveryDuration ? Number(values.deliveryDuration) : null;
    }
    return params;
  };

  const exportPdf = async () => {
    const pdf = new jsPDF("p", "pt", [842, 595]);
    pdf.addFileToVFS("Roboto-Regular.ttf", roboto);
    pdf.addFont("Roboto-Regular.ttf", "Roboto", "normal");
    pdf.setFont("Roboto");
    pdf.setFontSize(12);

    const string = renderToStaticMarkup(<OrderExportTemplate pdf={pdf} order={order} setting={setting} />);
    pdf.html(string, {
      callback: function (pdf) {
        const filename = `ORDER_DETAILS_${order?.orderCode}.pdf`;
        pdf.save(filename);
      },
      x: 0,
      y: 0,
      html2canvas: {
        width: 170,
      },
    });
  };

  if ((id && order?._id !== id) || (orderCode && order?.orderCode !== orderCode))
    return <Spin style={{ paddingTop: 100, width: "100%" }} />;

  const logs = order?.logs || [];
  const statusDatetime = status => logs.find(l => l.status === status)?.createdAt;

  const errorDetails = [...order.errorDetails];
  pullAll(errorDetails, [
    "Pickup lat must be a latitude string or number",
    "Pickup lng must be a longitude string or number",
    "Delivery lat must be a latitude string or number",
    "Delivery lng must be a longitude string or number",
  ]);

  const driverNotes = (order.logs || [])
    .map(l => l.notes)
    .filter(note => note && note.trim() !== "")
    .join("; ");

  return (
    <div className="Content OrdersDetailPage">
      <div className="ContentHeader">
        <div className="HeaderTitle">
          <Link to={"/orders"}>{getTranslation(setting, "Orders", "Orders")}</Link>
          <ReactSVG className="icon" src="/icons/arrow-forward.svg" />
          <span className="Link">{order.orderCode}</span>
        </div>
      </div>
      {order.errorDetails.length > 0 && (
        <div className="ContentErrors">
          {errorDetails.map((error, index) => {
            return (
              <div key={index} className="errorDetail">
                {error}
              </div>
            );
          })}
          {intersection(order.errorDetails, [
            "Pickup lat must be a latitude string or number",
            "Pickup lng must be a longitude string or number",
          ]).length > 0 && (
            <div key="Pickup Address" className="item">
              Pickup address is not valid
            </div>
          )}
          {intersection(order.errorDetails, [
            "Delivery lat must be a latitude string or number",
            "Delivery lng must be a longitude string or number",
          ]).length > 0 && (
            <div key="Delivery Address" className="item">
              Delivery address is not valid
            </div>
          )}
        </div>
      )}
      <div className="ContentMain">
        <div className="MainHeader">
          <div className="HeaderLeft">{order.orderCode}</div>
          {canEdit && (
            <div className="HeaderRight">
              <Button type="primary" onClick={() => handleClickAction("order_info", order)}>
                {getTranslation(setting, "Common", "Edit")}
                <ReactSVG className="icon edit" src="/icons/edit.svg" />
              </Button>
              <Button onClick={exportPdf}>
                <ReactSVG className="icon" src="/icons/share.svg" />
              </Button>
            </div>
          )}
        </div>
        <Row gutter={24}>
          <Col span={4}>
            <div className="Info">
              <label>{getTranslation(setting, "Orders", "Order Ref")}</label>
              <div className="Text">{order.orderRef || order.orderCode}</div>
            </div>
          </Col>
          <Col span={4}>
            <div className="Info">
              <label>{getTranslation(setting, "Orders", "Date")}</label>
              <div className="Text">
                {order.deliveryDate ? moment(order.deliveryDate).utc().format(DATE_FORMAT_FRONTEND) : "-"}
              </div>
            </div>
          </Col>
          <Col span={4}>
            <div className="Info">
              <label>{getTranslation(setting, "Orders", "Status")}</label>
              <span className={"status orderStatus " + status}>
                {getTranslation(setting, "Orders", STATUS.find(s => s.code === order.status)?.name)}
              </span>
            </div>
          </Col>
          <Col span={4}>
            <div className="Info">
              <label>{getTranslation(setting, "Orders", "User Group")}</label>
              <div className="Text">{order.group?.name}</div>
            </div>
          </Col>
          <Col span={4}>
            <div className="Info">
              <label>{getTranslation(setting, "Orders", "Load")}</label>
              <div className="Text">{order.load}</div>
            </div>
          </Col>
          <Col span={4}>
            <div className="Info">
              <label>{getTranslation(setting, "Orders", "Driver")}</label>
              <div className="Text">{order.driver?.name}</div>
            </div>
          </Col>
        </Row>
        {user && (
          <div className="Info">
            <label>{getTranslation(setting, "Orders", "Labels")}</label>
            <div className="Text">
              <LabelComponent labelIds={order.labels} isEdit={false} />
            </div>
          </div>
        )}
        <Row gutter={24}>
          <Col span={8}>
            <div className="Info">
              <label>{getTranslation(setting, "Orders", "Types")}</label>
              <div className="Text Types">
                {order.requiredSkills ? (
                  <>
                    {order.requiredSkills.map(type => (
                      <div key={type} className="Type">
                        {type}
                      </div>
                    ))}
                  </>
                ) : (
                  "-"
                )}
              </div>
            </div>
          </Col>
          {order.routePlanType === "PDP" && (
            <Col span={8}>
              <div className="Info">
                <label>{getTranslation(setting, "Orders", "Pickup Notes")}</label>
                <div className="Text">{order.pickupNotes}</div>
              </div>
            </Col>
          )}
          <Col span={8}>
            <div className="Info">
              <label>{getTranslation(setting, "Orders", "Delivery Notes")}</label>
              <div className="Text">{order.deliveryNotes ?? order.notes}</div>
            </div>
          </Col>
        </Row>
        <Row gutter={24}>
          {Object.keys(order.fields || {}).map((field, index) => (
            <Col key={index} span={4}>
              <div className="Info">
                <label>{field}</label>
                <div className="Text">{order.fields[field]}</div>
              </div>
            </Col>
          ))}
        </Row>
        <div className="Info Driver">
          <label>{getTranslation(setting, "Orders", "Driver")}: </label>
          <div className="Text">
            <img src={markerIcons["driver"][order.driver?.color]} alt={order.driver?.name} />
            {order.driver?.name || "Unassigned"}
          </div>
        </div>
        <div className="Info Progress">
          <Steps current={getProgressStatus(order?.status)} progressDot>
            <Step
              title={<div className="status ASSIGNED">{getTranslation(setting, "Orders", "Assigned")}</div>}
              description={
                statusDatetime("ASSIGNED") && moment(statusDatetime("ASSIGNED")).format(DATETIME_FORMAT_FRONTEND)
              }
            />
            {order?.status === "SKIPPED" && (
              <Step
                title={<div className="status SKIPPED">{getTranslation(setting, "Orders", "Skipped")}</div>}
                description={
                  statusDatetime("SKIPPED") && moment(statusDatetime("SKIPPED")).format(DATETIME_FORMAT_FRONTEND)
                }
              />
            )}
            {order?.status === "PICKUP_ARRIVED" && (
              <Step
                title={
                  <div className="status PICKUP_ARRIVED">{getTranslation(setting, "Orders", "Pickup Arrived")}</div>
                }
                description={
                  statusDatetime("PICKUP_ARRIVED") &&
                  moment(statusDatetime("PICKUP_ARRIVED")).format(DATETIME_FORMAT_FRONTEND)
                }
              />
            )}
            {order?.status === "PICKUP_FAILED" && (
              <Step
                title={<div className="status PICKUP_FAILED">{getTranslation(setting, "Orders", "Orders")}</div>}
                description={
                  statusDatetime("PICKUP_FAILED") &&
                  moment(statusDatetime("PICKUP_FAILED")).format(DATETIME_FORMAT_FRONTEND)
                }
              />
            )}
            <Step
              title={<div className="status COLLECTED">{getTranslation(setting, "Orders", "Collected")}</div>}
              description={
                statusDatetime("COLLECTED") && moment(statusDatetime("COLLECTED")).format(DATETIME_FORMAT_FRONTEND)
              }
            />
            <Step
              title={<div className="status IN_TRANSIT">{getTranslation(setting, "Orders", "In Transit")}</div>}
              description={
                statusDatetime("IN_TRANSIT") && moment(statusDatetime("IN_TRANSIT")).format(DATETIME_FORMAT_FRONTEND)
              }
            />
            {order?.status === "DELIVERY_ARRIVED" && (
              <Step
                title={
                  <div className="status DELIVERY_ARRIVED">{getTranslation(setting, "Orders", "Delivery Arrived")}</div>
                }
                description={
                  statusDatetime("DELIVERY_ARRIVED") &&
                  moment(statusDatetime("DELIVERY_ARRIVED")).format(DATETIME_FORMAT_FRONTEND)
                }
              />
            )}
            {order?.status === "DELIVERY_FAILED" && (
              <Step
                title={
                  <div className="status DELIVERY_FAILED">{getTranslation(setting, "Orders", "Delivery Failed")}</div>
                }
                description={
                  statusDatetime("DELIVERY_FAILED") &&
                  moment(statusDatetime("DELIVERY_FAILED")).format(DATETIME_FORMAT_FRONTEND)
                }
              />
            )}
            <Step
              title={<div className="status COMPLETED">{getTranslation(setting, "Orders", "Completed")}</div>}
              description={
                statusDatetime("COMPLETED") && moment(statusDatetime("COMPLETED")).format(DATETIME_FORMAT_FRONTEND)
              }
            />
          </Steps>
        </div>
      </div>
      <div className="ContentLocations">
        <Row gutter={24}>
          {order.routePlanType === "PDP" && (
            <Col span={12}>
              <div className="ItemLocation StartLocation">
                <div className="LocationHeader">
                  <div className="HeaderLeft">
                    <div className="Type Pickup">{getTranslation(setting, "Orders", "Pickup")}</div>
                    {order.pickupName || "-"}
                  </div>
                  {canEdit && (
                    <div className="HeaderRight">
                      <Button type="link" onClick={() => handleClickAction("pickup", order)}>
                        {getTranslation(setting, "Common", "Edit")}
                        <ReactSVG className="icon edit" src="/icons/edit.svg" />
                      </Button>
                      {order.routePlan && (
                        <Link to={`/route-plans/${order.routePlan}?stopId=${order._id}-pickup`}>
                          <Button type="link">
                            {getTranslation(setting, "Orders", "View on Route Plan")}
                            <ReactSVG className="icon" src="/icons/view-on-route.svg" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  )}
                  {!canEdit && order.routePlan && (
                    <Link to={`/route-plans/${order.routePlan}/orders?stopId=${order._id}-pickup`}>
                      <Button type="link">
                        <ReactSVG className="icon" src="/icons/map.svg" />
                      </Button>
                    </Link>
                  )}
                </div>
                <div className="Info">
                  <label>{getTranslation(setting, "Orders", "Address")}</label>
                  <div className="Text">{order.pickupAddress || "-"}</div>
                </div>
                <Row gutter={24}>
                  <Col span={6}>
                    <div className="Info Email">
                      <label>{getTranslation(setting, "Orders", "Email")}</label>
                      <div className="Text">{order.pickupEmail || "-"}</div>
                    </div>
                  </Col>
                  <Col span={6}>
                    <div className="Info">
                      <label>{getTranslation(setting, "Orders", "Phone Number")}</label>
                      <div className="Text">{order.pickupPhone || "-"}</div>
                    </div>
                  </Col>
                  <Col span={6}>
                    <div className="Info">
                      <label>{getTranslation(setting, "Orders", "Time Window")}</label>
                      <div className="Text">{(order.timeStart || "-") + " - " + (order.timeEnd || "-")}</div>
                    </div>
                  </Col>
                  <Col span={6}>
                    <div className="Info">
                      <label>{getTranslation(setting, "Orders", "Duration")}</label>
                      <div className="Text">{order.pickupDuration || "-"}</div>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          )}
          <Col span={12}>
            <div className="ItemLocation EndLocation">
              <div className="LocationHeader">
                <div className="HeaderLeft">
                  <div className="Type Delivery">{getTranslation(setting, "Orders", "Delivery")}</div>
                  {order.deliveryName || "-"}
                </div>
                {canEdit && (
                  <div className="HeaderRight">
                    <Button type="link" onClick={() => handleClickAction("delivery", order)}>
                      {getTranslation(setting, "Common", "Edit")}
                      <ReactSVG className="icon edit" src="/icons/edit.svg" />
                    </Button>
                    {order.routePlan && (
                      <Link to={`/route-plans/${order.routePlan}?stopId=${order._id}-delivery`}>
                        <Button type="link">
                          {getTranslation(setting, "Orders", "View on Route Plan")}
                          <ReactSVG className="icon" src="/icons/view-on-route.svg" />
                        </Button>
                      </Link>
                    )}
                  </div>
                )}
                {!canEdit && order.routePlan && (
                  <Link to={`/route-plans/${order.routePlan}/orders?stopId=${order._id}-delivery`}>
                    <Button type="link">
                      <ReactSVG className="icon" src="/icons/map.svg" />
                    </Button>
                  </Link>
                )}
              </div>
              <div className="Info Address">
                <label>{getTranslation(setting, "Orders", "Address")}</label>
                <div className="Text">{order.deliveryAddress || "-"}</div>
              </div>
              <Row gutter={24}>
                <Col span={6}>
                  <div className="Info Email">
                    <label>{getTranslation(setting, "Orders", "Email")}</label>
                    <div className="Text">{order.deliveryEmail || "-"}</div>
                  </div>
                </Col>
                <Col span={6}>
                  <div className="Info Phone">
                    <label>{getTranslation(setting, "Orders", "Phone Number")}</label>
                    <div className="Text">{order.deliveryPhone || "-"}</div>
                  </div>
                </Col>
                <Col span={6}>
                  <div className="Info">
                    <label>{getTranslation(setting, "Orders", "Time Window")}</label>
                    <div className="Text">
                      {(order.deliveryTimeStart || "-") + " - " + (order.deliveryTimeEnd || "-")}
                    </div>
                  </div>
                </Col>
                <Col span={6}>
                  <div className="Info">
                    <label>{getTranslation(setting, "Orders", "Duration")}</label>
                    <div className="Text">{order.deliveryDuration || "-"}</div>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </div>
      <div className="ContentMore">
        <Row gutter={24}>
          <Col span={12}>
            <div className="ItemMore Map">
              <StopsMapbox
                routes={[]}
                stops={filter(stops, s => s.lat && s.lng)}
                orders={[]}
                stopClicked={stopClicked}
                onClickStop={setStopClicked}
                onClosePopup={onClosePopup}
              />
            </div>
          </Col>
          <Col span={12}>
            <div className="ItemMore PopSignature">
              <Row>
                <Col span={12}>
                  <div className="Item Pop">
                    <label>{getTranslation(setting, "Orders", "POD")}</label>
                    <div className="ItemContent">
                      <Row gutter={[12, 6]}>
                        {order?.pods.slice(0, 4).map((pod, index) => (
                          <Col key={index} span={12}>
                            <Image
                              preview={{ visible: false }}
                              width="100%"
                              src={`${order?.podUrl}/${pod}`}
                              onClick={() => {
                                setVisible(true);
                                setCurrentImageIndex(index);
                              }}
                            />
                          </Col>
                        ))}
                      </Row>
                      {order?.pods.length > 4 && (
                        <div
                          className="ViewMore"
                          onClick={() => {
                            setVisible(true);
                            setCurrentImageIndex(3);
                          }}>
                          +{order?.pods.length - 4}
                        </div>
                      )}
                    </div>
                    <div style={{ display: "none" }}>
                      <Image.PreviewGroup
                        preview={{
                          visible,
                          onOpenChange: vis => setVisible(vis),
                          current: currentImageIndex,
                        }}>
                        {order?.pods.map((pod, index) => (
                          <Image key={index} src={`${order?.podUrl}/${pod}`} />
                        ))}
                      </Image.PreviewGroup>
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="Item Signature">
                    <label>{getTranslation(setting, "Orders", "Signature")}</label>
                    <div className="ItemContent">
                      {order?.signature && <img src={`data:image/jpeg;base64,${order?.signature}`} />}
                    </div>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col span={12}>
                  <div className="Item DriverNotes DriverNotes-left">
                    <label>{getTranslation(setting, "Orders", "Driver notes")}</label>
                    <div className="ItemContent">{driverNotes ?? "N/A"}</div>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="Item DriverNotes">
                    <label>{getTranslation(setting, "Orders", "Package was")}</label>
                    <div className="ItemContent">{order?.packageWas ?? "N/A"}</div>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </div>
      <Modal open={state.visibleModal} onCancel={handleCancel} forceRender footer={[]} width={900}>
        {state.action === "order_info" && (
          <OrderInfoForm
            form={form}
            isFetching={isFetching}
            groups={groups}
            handleSubmit={handleSubmit}
            handleCancel={handleCancel}
          />
        )}
        {state.action === "pickup" && (
          <PickupForm
            form={form}
            isFetching={isFetching}
            handleSubmit={handleSubmit}
            handleCancel={handleCancel}
            countryCodeInit={countryCodeInit}
          />
        )}
        {state.action === "delivery" && (
          <DeliveryForm
            form={form}
            isFetching={isFetching}
            handleSubmit={handleSubmit}
            handleCancel={handleCancel}
            countryCodeInit={countryCodeInit}
          />
        )}
      </Modal>
    </div>
  );
};

export default withPage(ItemContainer(OrdersDetailPage));
